import type {
  CustomizationGroup,
  CustomizationValue,
  Product,
} from "@/lib/types";

export type SelectedOption = {
  groupId: string;
  groupName: string;
  valueId: string;
  valueLabel: string;
  adjustmentType: CustomizationValue["adjustmentType"];
  adjustmentAmount: number;
  appliedCents: number;
};

export function getCustomizationPriceCents(
  product: Product,
  value: CustomizationValue,
) {
  if (value.adjustmentType === "fixed") {
    return value.adjustmentAmount;
  }
  if (value.adjustmentType === "percent") {
    return Math.round((product.basePriceCents * value.adjustmentAmount) / 100);
  }
  return 0;
}

export function formatCustomizationPrice(product: Product, value: CustomizationValue) {
  const appliedCents = getCustomizationPriceCents(product, value);
  if (appliedCents === 0) {
    return "Incluido";
  }
  return `${appliedCents > 0 ? "+" : "−"}${formatMXN(Math.abs(appliedCents))}`;
}

export function getPriceBreakdown(
  product: Product,
  groups: CustomizationGroup[],
  selections: Record<string, string>,
) {
  const selectedOptions: SelectedOption[] = [];

  for (const groupId of product.customizationGroupIds) {
    const group = groups.find((entry) => entry.id === groupId && entry.active);
    if (!group) {
      continue;
    }

    const value = group.values.find(
      (entry) => entry.id === selections[group.id],
    );
    if (!value) {
      continue;
    }

    const appliedCents = getCustomizationPriceCents(product, value);

    selectedOptions.push({
      groupId: group.id,
      groupName: group.name,
      valueId: value.id,
      valueLabel: value.label,
      adjustmentType: value.adjustmentType,
      adjustmentAmount: value.adjustmentAmount,
      appliedCents,
    });
  }

  const totalCents = Math.max(
    0,
    product.basePriceCents +
      selectedOptions.reduce((sum, option) => sum + option.appliedCents, 0),
  );

  return { totalCents, selectedOptions };
}

export function formatMXN(cents: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(cents / 100);
}
